export const DARK = "#222222"
export const WHITE = "#FEFEFE"

/** Color picker quick access */
export const SAVED_COLORS = [
  "#EF4444", // Red
  "#F97316", // Orange
  "#FACC15", // Yellow
  "#4ADE80", // Lime green
  "#2DD4BF", // Turquoise
  "#3B82F6", // Royal blue
  "#6467F2", // Purple
  "#EC4899", // Hot pink
  "#F43F5E", // Coral pink
  "#D946EF", // Bright purple
  "#8B5CF6", // Lavender
  "#0EA5E9", // Sky blue
  "#10B981", // Emerald green
  "#84CC16", // Yellow-green
]

/** Subtle (light) colors */
export const LIGHT_COLORS = [
  "#E6E9F4", // Pale lavender
  "#DBECFC", // Light sky blue
  "#DBF8FC", // Pale cyan
  "#DCF7E0", // Mint green
  "#EDFBCE", // Pale lime
  "#FFF6C9", // Light yellow
  "#FCE7DB", // Peach
  "#FFD5D5", // Light pink
  "#FFE0F4", // Pale rose
  "#F3E4FF", // Light lilac
]

const RANDOM_COLORS = [DARK].concat(SAVED_COLORS)

/** Get a (random) color. Provide an `offset` to cycle through the colors (optional) */
export function getRandomColor(): string
export function getRandomColor(offset: number): string
export function getRandomColor(offset?: number): string {
  if (offset === undefined) {
    return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)]
  }
  return RANDOM_COLORS[offset % RANDOM_COLORS.length]
}

// Determine "brightness" of a color. Brightness of `0` is black and `255` is white.
// https://24ways.org/2010/calculating-color-contrast
export function getBrightness(hexColor: string): number {
  const trimmedHex = hexColor.trim().replace("#", "")

  const sixDigitHex =
    trimmedHex.length === 3
      ? trimmedHex
          .split("")
          .map((char) => char + char)
          .join("")
      : trimmedHex

  const r = parseInt(sixDigitHex.slice(0, 2), 16)
  const g = parseInt(sixDigitHex.slice(2, 4), 16)
  const b = parseInt(sixDigitHex.slice(4, 6), 16)

  return (r * 299 + g * 587 + b * 114) / 1000
}

// Determine `text` color to use on a `hexColor` background
export function getContrastColor(hexBackground: string): string {
  const brightness = getBrightness(hexBackground)

  // Return white text color for dark backgrounds and vice-versa
  return brightness < 148 ? WHITE : DARK
}

// Determine if chosen `hexColor` is hard to read on a `white` background
export function isTooBrightOnWhiteBackground(hexColor: string): boolean {
  return getBrightness(hexColor) > 210
}

// Track colors per conversation
const assignedColorsMap = new Map<string, Record<string, string>>()

// Tailwind colors for health professionals
const professionalColors = [
  "text-chat-orange",
  "text-chat-green",
  "text-chat-purple",
  "text-chat-lavender",
  "text-chat-red",
] as const

// Tailwind colors for patients (these should not overlap with the professional
// colors so that users/patients can easily distinguish between the two)
const patientColors = ["text-blue-400"] as const

export const getUserColor = (conversationId: string, isPatient: boolean, userName: string) => {
  if (isPatient) {
    return patientColors[0] // Patients always get blue
  }

  // Initialize tracking for this conversation if needed
  if (!assignedColorsMap.has(conversationId)) {
    assignedColorsMap.set(conversationId, {})
  }

  const conversationColors = assignedColorsMap.get(conversationId)!

  // If this user already has an assigned color
  if (conversationColors[userName]) {
    return conversationColors[userName]
  }

  // Otherwise assign the next available color
  const usedColors = Object.values(conversationColors)
  const availableColor = professionalColors.find((color) => !usedColors.includes(color)) || professionalColors[0]

  // Store the assignment
  conversationColors[userName] = availableColor

  return availableColor
}
